import { useEffect, useRef, useState } from 'react';

const DEFAULT_DURATION_MS = 1200;
/** Число уже на экране и меняется (другой период): итог нужен быстро — потолок анимации из design-system.md §1. */
const DEFAULT_UPDATE_DURATION_MS = 300;

interface UseCountUpOptions {
  /** Сколько длится первый набор от нуля, мс. */
  durationMs?: number;
  /** Сколько длится переход, когда число уже на экране, мс. */
  updateDurationMs?: number;
  /** Знаков после запятой: промежуточные значения округляются так же, как итоговое. */
  precision?: number;
}

const easeOutCubic = (progress: number) => 1 - (1 - progress) ** 3;

const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Значение, которое «набегает» от нуля до `target`, как цифры на счётчике: быстро в начале и мягко у конца.
 *
 * - На первый показ набор идёт от нуля (1,2 с); когда `target` меняется (другой период, фильтр), число доезжает от
 *   того, что на экране, а не сбрасывается в ноль, и за 300 мс — пользователь ждёт итоговую цифру.
 * - `prefers-reduced-motion` — сразу итоговое значение, без движения.
 * - Конечное значение всегда точное: последний кадр ставит `target`, а не округлённое промежуточное.
 */
export function useCountUp(
  target: number,
  { durationMs = DEFAULT_DURATION_MS, updateDurationMs = DEFAULT_UPDATE_DURATION_MS, precision = 0 }: UseCountUpOptions = {},
): number {
  const [isReduced] = useState(prefersReducedMotion);
  const [value, setValue] = useState(isReduced ? target : 0);
  const currentRef = useRef(isReduced ? target : 0);

  useEffect(() => {
    if (isReduced || !Number.isFinite(target)) {
      currentRef.current = target;
      setValue(target);
      return undefined;
    }

    const from = currentRef.current;
    const delta = target - from;
    if (delta === 0) return undefined;

    // От нуля — первый показ, неспешный; от значения на экране — смена периода, короткая.
    const duration = from === 0 ? durationMs : updateDurationMs;

    // Отсчёт — от метки первого кадра, а не от `performance.now()`: так обе отметки из одних часов.
    let startedAt: number | undefined;
    let frame = 0;

    const tick = (now: number) => {
      startedAt ??= now;
      const progress = Math.min((now - startedAt) / duration, 1);

      if (progress >= 1) {
        currentRef.current = target;
        setValue(target);
        return;
      }

      currentRef.current = from + delta * easeOutCubic(progress);
      setValue(Number(currentRef.current.toFixed(precision)));
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(frame);
  }, [target, durationMs, updateDurationMs, precision, isReduced]);

  return value;
}
